import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FreelancerWizard from '../components/FreelancerWizard'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Partner = {
  id: number
  code: string
  name: string
  partner_type: 'CUSTOMER' | 'VENDOR' | 'BOTH' | 'FREELANCER'
  email: string
  country_code: string
  tax_id: string
  is_active: boolean
}

// Minimal create payload — code is auto-allocated server-side.
// Address, contact, currency, country all completed on the record page.
type CreateInput = {
  name: string
  partner_type: 'CUSTOMER' | 'VENDOR' | 'BOTH'
  tax_id: string
}

const TYPE_BADGE: Record<Partner['partner_type'], string> = {
  CUSTOMER: 'bg-blue-100 text-blue-800',
  VENDOR: 'bg-amber-100 text-amber-800',
  BOTH: 'bg-purple-100 text-purple-800',
  FREELANCER: 'bg-teal-100 text-teal-800',
}

const TYPE_LABEL: Record<Partner['partner_type'], string> = {
  CUSTOMER: 'Customer',
  VENDOR: 'Vendor',
  BOTH: 'Both',
  FREELANCER: 'Freelancer',
}

const TYPE_FILTERS = ['ALL', 'CUSTOMER', 'VENDOR', 'FREELANCER'] as const

export default function FinancePartners() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const { data, isLoading } = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterType, setFilterType] = useState<typeof TYPE_FILTERS[number]>('ALL')

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Partner>('/partners/', {
      name: draft.name,
      partner_type: draft.partner_type,
      tax_id: draft.tax_id,
      is_active: true,
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['finance', 'partners'] })
      toast.success('Partner created')
      setCreating(false)
      navigate(`/admin/partners/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/partners/${id}/`),
    onSuccess: () => {
      toast.success('Partner deleted')
      qc.invalidateQueries({ queryKey: ['finance', 'partners'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const partners = data || []
  // 'BOTH' partners qualify under both CUSTOMER and VENDOR filters; FREELANCERs are separate.
  const filtered = filterType === 'ALL'
    ? partners
    : partners.filter((p) => p.partner_type === filterType || (filterType !== 'FREELANCER' && p.partner_type === 'BOTH'))

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Partners</h2>
          <p className="text-sm text-muted-foreground">{partners.length} total</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setWizardOpen(true)}>
            <Plus className="h-3 w-3 mr-1" /> New Freelancer
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-3 w-3 mr-1" /> Add partner
          </Button>
        </div>
      </div>

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Filter:</span>
        {TYPE_FILTERS.map((t) => (
          <button
            key={t}
            onClick={() => setFilterType(t)}
            className={`px-3 py-1 rounded ${filterType === t ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}
          >
            {t === 'ALL' ? 'All' : t === 'CUSTOMER' ? 'Customers' : t === 'VENDOR' ? 'Vendors' : 'Freelancers'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No partners yet. Click "+ Add partner" to create one.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th>Email</Th>
                <Th>Country</Th>
                <Th>Tax ID</Th>
                <Th>Active</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/partners/${p.id}`)}
                >
                  <Td className="font-mono text-xs">{p.code}</Td>
                  <Td className="font-medium">{p.name}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${TYPE_BADGE[p.partner_type]}`}>
                      {TYPE_LABEL[p.partner_type]}
                    </span>
                  </Td>
                  <Td className="text-xs">{p.email || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="font-mono text-xs">{p.country_code || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="font-mono text-xs">{p.tax_id || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">
                    {p.is_active
                      ? <span className="text-green-700">Active</span>
                      : <span className="text-muted-foreground">Inactive</span>}
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/partners/${p.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(
                            `partner ${p.code} (${p.name})`,
                            () => deleteMut.mutate(p.id),
                          )}
                          className="text-destructive"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer - minimal fields. Everything else on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New partner"
        description="Legal name, type, and Tax ID. Code is auto-generated. All other details (address, contact, currency, VAT) are completed on the partner record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="pt-name">Legal name *</FieldLabel>
            <Input id="pt-name" autoFocus required value={draft.name}
              onChange={(ev) => setDraft({ ...draft, name: ev.target.value })} />
          </Field>

          <Field>
            <FieldLabel htmlFor="pt-type">Partner type</FieldLabel>
            <Select value={draft.partner_type}
              onValueChange={(v) => setDraft({ ...draft, partner_type: v as CreateInput['partner_type'] })}>
              <SelectTrigger id="pt-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CUSTOMER">Customer</SelectItem>
                <SelectItem value="VENDOR">Vendor</SelectItem>
                <SelectItem value="BOTH">Both (customer &amp; vendor)</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="pt-tax">Tax ID</FieldLabel>
            <Input id="pt-tax" value={draft.tax_id}
              onChange={(ev) => setDraft({ ...draft, tax_id: ev.target.value })}
              className="font-mono" placeholder="Fiscal / tax identification number" />
          </Field>
        </FieldGroup>
      </RecordDrawer>

      {/* Freelancer onboarding wizard — creates Partner + VendorContract + Employee. */}
      <FreelancerWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={({ partner_id }) => {
          qc.invalidateQueries({ queryKey: ['finance', 'partners'] })
          qc.invalidateQueries({ queryKey: ['finance', 'vendor-contracts'] })
          qc.invalidateQueries({ queryKey: ['hr', 'employees'] })
          toast.success('Freelancer onboarded')
          setWizardOpen(false)
          navigate(`/admin/partners/${partner_id}`)
        }}
      />
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return { name: '', partner_type: 'CUSTOMER', tax_id: '' }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
